import { Alert, Button, Col, Row, Spin, List, Typography, Tooltip, Form, Input } from 'antd'
import _ from 'lodash'
import BaseModal from 'components/BaseModal'
import { DeleteIcon, GitHubIcon, InfoIcon, LoadingIcon } from 'components/Icons'
import React, { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import ToolsIntegrationsAPI from 'restAPI/ToolsIntegrationAPI'
import GithubEnterpriseForm from './GithubEnterpriseForm'
import { BASE_GUTTER } from 'constants/Layout'
import ModalUtil from 'utils/modalUtil'
import DeletePopup from 'components/DeletePopup'
import message from 'components/CustomMessage'
import IntegrationsAPI from 'restAPI/IntegrationsAPI'
import UserAPI from 'restAPI/UserAPI'
import { useActiveUserContext } from 'contexts/ActiveUserContext'
import AppLoader from 'components/AppLoader'
import moment from 'moment'
import { urlRegexValidator } from 'utils/formUtils';
import useEnvironmentVariables from 'hooks/useEnvironmentVariables'
import AutoMappingSettingsForm, { GIT_TOOL_MAPPING_CONFIG } from '../common/AutoMappingSettingsForm'
import { useFeatureFlag } from 'contexts/FeatureFlagContext'

export default forwardRef(function GitInstallationPupop({installationId, hostUrl = "https://github.com/settings", accountType, account, toolName, ...props}, ref) {
    const [intervalId, setIntervalId] = useState();
    const configID = useRef();
    const toolConfigs = useRef();
    const featureFlags = useFeatureFlag();

    useEffect(() => {
        return () => clearInterval(intervalId);
    }, [intervalId]);

    const baseModalProps = {
        title: "GitHub Integration",
        footer: null
    }

    const saveSettings = useCallback(async () => {
        let configs = toolConfigs.current;
    
        const payload = {
            subProductMappingConfig: featureFlags.SIMPLIFIED_ONBOARDING ?
              _.filter([
                GIT_TOOL_MAPPING_CONFIG.MAP_INACTIVE_REPOS,
                GIT_TOOL_MAPPING_CONFIG.MAP_DORMANT_REPOS
              ], (entry) => configs?.[entry]) : [],
              codeClassificationEnabled: configs?.codeClassificationEnabled,
              githubIssuesEnabled: configs?.githubIssuesEnabled,
              ingestComponents: configs?.ingestComponents
            // autoCreateGitToolConfig: _.filter([
            //   AUTO_CREATE_GIT_TOOL_CONFIG.DEPENDABOT,
            //   AUTO_CREATE_GIT_TOOL_CONFIG.GITHUB_CODE_SCAN,
            //   AUTO_CREATE_GIT_TOOL_CONFIG.GITHUB_SECRET_SCAN
            // ], (entry) => configs?.[entry]),
          }
      
        await ToolsIntegrationsAPI.getGithubInstallationConfigs(configID.current, payload).promise;
      }, [featureFlags.SIMPLIFIED_ONBOARDING]);
    
    const  GIT_APP_NAME = useEnvironmentVariables('REACT_APP_GITHUB_APP_NAME');
    const openGitHubAppPage = () => {
        let url = `https://github.com/apps/${GIT_APP_NAME}`;
        if(installationId) {
            url = accountType === "Organization" 
                ? `${hostUrl}/organizations/${account}/settings/installations/${installationId}`
                : `${hostUrl}/settings/installations/${installationId}`;
        }
        const _window = window.open(url, "_blank", "width=1000,height=800,rel=noopener");
        var timer = setInterval(async function() { 
            if (!installationId && _window.location.pathname.indexOf("github/postInstallation") !== -1) {
                configID.current = JSON.parse(_window.document.body.innerText);
                if (configID.current) {
                    _window.close();
                }
            }
            if(_window.closed) {
                clearInterval(timer);
                if (featureFlags.SIMPLIFIED_ONBOARDING && !installationId) {
                    await saveSettings();
                }
                ModalUtil.closeAll();
            }
        }, 1000);
        setIntervalId(timer);
    }

    const onSettingsChange = useCallback((changedSetting, settings) => {
        toolConfigs.current = settings;
    }, [])

    return (
        <BaseModal
            ref={ref}
            modalProps={baseModalProps}
            afterClose={props.afterClose}
        >
            {props.unlinkInstallation && <Alert type='warning' className='m-b-sm' message="This will also remove the original installation"/>}
            <Alert 
                message="ArmorCode's Custom GitHub App integration offers flexible, 
                         customizable access and permission control for GitHub repositories, 
                         ideal for enterprises needing detailed integration management."
                className="m-b-sm"
            />
            {!installationId && <AutoMappingSettingsForm onChange={onSettingsChange} toolName={toolName}/>}
            <Button onClick={openGitHubAppPage} className="d-flex align-items-center"><GitHubIcon style={{fontSize: "1.3em"}} /> &nbsp; {installationId ? "Disconnect" : "Connect to"} GitHub</Button>
        </BaseModal>
    )
})


const GitEnterpriseInstallationPupop = forwardRef(function GitEnterpriseInstallationPupop( props, ref) {
    const [gitData, setGitData] = useState(null);
    const [intervalId, setIntervalId] = useState();
    const [selectedInstallation, setSelectedInstalltion] = useState('list');
    const [generatedKey, setGeneratedKey] = useState(); 
    const [appCreated, setAppCreated] = useState(false);
    const [formData, setFormData] = useState({});
    const [form] = Form.useForm();
    const [disableGithubForm, setDisableGithubForm] = useState(true)
    const toolConfigs = useRef();
    const configID = useRef();
    const featureFlags = useFeatureFlag();

    const fetchData = () => {
        const {promise, cancel} = ToolsIntegrationsAPI.getGithubEnterpriseIntegrationDetails();
        setGitData(null);
        promise.then(
            data => setGitData(_.isEmpty(data) ? [] : data)
        )

        return cancel;
    }

    useEffect(fetchData, []);
    useEffect(() => {
        return () => clearInterval(intervalId);
    }, [intervalId]);

    const baseModalProps = {
        title: "Custom GitHub App Integration",
        footer: null
    }

    const saveSettings = useCallback(async () => {
        let configs = toolConfigs.current;
    
        const payload = {
            subProductMappingConfig: featureFlags.SIMPLIFIED_ONBOARDING ?
              _.filter([
                GIT_TOOL_MAPPING_CONFIG.MAP_INACTIVE_REPOS,
                GIT_TOOL_MAPPING_CONFIG.MAP_DORMANT_REPOS
              ], (entry) => configs?.[entry]) : [],
              codeClassificationEnabled: configs?.codeClassificationEnabled,
              githubIssuesEnabled: configs?.githubIssuesEnabled,
              ingestComponents: configs?.ingestComponents
          }
      
        await ToolsIntegrationsAPI.getGithubInstallationConfigs(configID.current, payload).promise;
      }, [featureFlags.SIMPLIFIED_ONBOARDING]);

    const onSettingsChange = useCallback((changedSetting, settings) => {
        toolConfigs.current = settings;
    }, [])

    const openGitHubEnterpriseAppPage = () => {
        let url;
        
        const installation = _.find(gitData, {id: selectedInstallation})

        if(installation && installation.appUrl) {
            url = installation.appUrl;
            const _window = window.open(url, "_blank", "width=1000,height=800,rel=noopener");
            var timer = setInterval(async function() { 
                if(_window.closed) {
                    clearInterval(timer);
                    setGitData(null);
                    if (configID.current && featureFlags.SIMPLIFIED_ONBOARDING) {
                        await saveSettings();
                    }
                    ModalUtil.closeAll();
                    fetchData();
                }
                
                if (_window.location.pathname.indexOf("github/postInstallation") !== -1) {
                    configID.current = JSON.parse(_window.document.body.innerText);
                    if (configID.current) {
                        _window.close();
                    }
                }
            }, 1000);
            setIntervalId(timer);
        }
    }

    const deleteConfigration = async (id) => {
        try {
            await ToolsIntegrationsAPI.deleteGithubEnterpriseDetails(id).promise
            message.success('Successfully deleted configuration.');
            fetchData()
        } catch (err) {
            console.error(err)
        }
    }

    const deletePopup = (file) => {
        ModalUtil.show({
            content: (
                <DeletePopup 
                    promptMessage={`You want to delete ${file.hostUrl}?`}
                    note={false}
                    confirmText={"Delete"}
                    onDelete={() => deleteConfigration(file.id)}
                />
            )
        })
    };

    const checkUrlValidation = _.debounce(async () => {
        const valid = await form.validateFields();
        setDisableGithubForm(!valid);
    }, 500)

    const handleChange = useCallback(values => {
        setFormData(values);
        checkUrlValidation();
    }, [checkUrlValidation])

    const showList =  selectedInstallation === "list"
    const isDisable = useMemo(() => {
        return (selectedInstallation === "list" || selectedInstallation === "new") && disableGithubForm 
    }, [disableGithubForm, selectedInstallation])
    return (
        <BaseModal
            ref={ref}
            modalProps={baseModalProps}
            afterClose={props.afterClose}
        >
            <Spin spinning={gitData === null} indicator={<LoadingIcon />}>
                <Row gutter={BASE_GUTTER}>
                    <Col span={24}>
                        <Alert 
                            message="ArmorCode's Custom GitHub App integration offers flexible, 
                                        customizable access and permission control for GitHub repositories, 
                                        ideal for enterprises needing detailed integration management."
                            className="m-b-sm"
                        />
                    </Col>
                    {
                        (showList || selectedInstallation === 'new') && !appCreated && <Col span={24}>
                            <Form layout="vertical" form={form} onValuesChange={handleChange}>
                                <Form.Item name="hostUrl" label="Host URL" rules={[{
                                        required: true,
                                        message: "Host Url is required"
                                    }, urlRegexValidator()]} >
                                    <Input placeholder="https://github.example.com" value={formData.hostUrl} />
                                </Form.Item>
                            </Form>
                        </Col>
                    }
                    <Col span={24}>
                        <div className={(showList || selectedInstallation === 'new') ? "d-flex justify-content-between inline-form-item" : "text-right"}>
                            {(showList || selectedInstallation === 'new') && <Typography.Text strong>{showList ? "Configuration List" : "Create Configuration"}</Typography.Text>}
                            <Button
                                disabled={isDisable}
                                type={showList ? "primary" : ""}
                                onClick={() => {
                                    setSelectedInstalltion(showList ? 'new' : 'list')
                                    if (appCreated) {
                                        setAppCreated(!appCreated)
                                    }
                                }}>
                                {showList ? "Create" : "Cancel"}
                            </Button>
                        </div>
                        {showList &&
                            <List
                                size="small"
                                dataSource={gitData ?? []}
                                renderItem={(item) => (
                                    <List.Item>
                                        <Col>
                                            <Typography.Link onClick={() => setSelectedInstalltion(item.id)} >{item.hostUrl}/{item.appId}</Typography.Link>
                                        </Col>
                                        <Col className="text-right">
                                            <DeleteIcon onClick={() => deletePopup(item)} className="danger" />
                                        </Col>
                                    </List.Item>
                                )
                                }
                            />
                        }
                    </Col>
                    
                    <Col span={24}>
                        {
                            selectedInstallation === "new" && appCreated ? <GithubEnterpriseForm initialValues={formData} generatedKey={generatedKey} afterSubmit={(d) => {
                                fetchData();
                                setSelectedInstalltion(d.id)
                            }} /> : selectedInstallation === "new" ? <GithubCreateAppForm setGeneratedKey={setGeneratedKey} setSelectedInstalltion={setSelectedInstalltion} setAppCreated={setAppCreated} formData={formData} /> : null
                        }
                    </Col>
                    {
                        selectedInstallation !== "new" && !showList ?<>
                            <AutoMappingSettingsForm onChange={onSettingsChange} toolName={props.toolName}/>
                            <Col span={24} offset={12} >
                                <Button onClick={openGitHubEnterpriseAppPage} className="d-flex align-items-center"><GitHubIcon style={{fontSize: "1.3em"}} /> &nbsp; Connect to Custom GitHub App</Button>
                            </Col>
                        </> : null
                    }
                </Row>
            </Spin>
        </BaseModal>
    )
})

export {GitEnterpriseInstallationPupop}

const GithubCreateAppForm = ({ setAppCreated, setGeneratedKey: setGeneratedKeyToParent, setSelectedInstalltion, formData }) => {
    const [intervalId, setIntervalId] = useState();
    const [generatedKey, setGeneratedKey] = useState();
    const [loading, setLoading] = useState(false);
    const activeUser = useActiveUserContext();

    useEffect(() => {
        return () => clearInterval(intervalId);
    }, [intervalId]);

    const onSave = useCallback(() => {
        const url = formData.hostUrl ? `${formData.hostUrl}/settings/apps/new` : "https://github.com/settings/apps/new";

        const _window = window.open(url, "_blank", "width=1000,height=800,rel=noopener");
        var timer = setInterval(function() { 
            if(_window?.closed) {
                setAppCreated(true);
                clearInterval(timer);
            }
        }, 1000);
        setIntervalId(timer);
    }, [setAppCreated, formData]);

    const assignableRolesParams = useMemo(() => {
        const { sessionData: { tenantScope, assoiatedTeamRoleMap={} }, role} = activeUser;
        let params = {};
        if (!tenantScope) {
                const [team] = assoiatedTeamRoleMap[role];
                params['teamId'] = team.id;
        }
        return params;
    }, [activeUser]);

    useEffect(() => {
        setLoading(true);
        const { promise, cancel } = UserAPI.getAssignableRoles(assignableRolesParams);

        promise.then((roles) => {
            let role = _.find(roles, { role: "Read Only" });
            if (_.isUndefined(role)) {
                role = roles[0];
            }
            const { promise, cancel } = IntegrationsAPI.createNewAPIKey({
                expiry: null,
                name: `GET-${moment().valueOf()}`,
                apiTokenType: "GITHUB_ENTERPRISE_TOKEN",
                productIds: [],
                accessOnAllProducts: true
            });

            promise.then(data => {
                setGeneratedKeyToParent(data);
                setGeneratedKey(data);
            }).catch(() => setSelectedInstalltion('list'))
            .finally(() => setLoading(false))

            return cancel;
        }).catch(() => setLoading(false));

        return cancel;
    }, [assignableRolesParams, setGeneratedKeyToParent, setSelectedInstalltion]);
    
    const postURL = useMemo(() => `${window.location.origin}/user/tools/github/postInstallation/${generatedKey?.apiKey || '{apiKey}'}`, [generatedKey?.apiKey]);
    const webHookURL = useMemo(() => `${window.location.origin}/public/webHookEvent/${generatedKey?.id || '{apiKeyID}' }`, [generatedKey?.id]);
    const secret = useMemo(() => `${generatedKey?.apiKey || ''}`, [generatedKey?.apiKey])

    return (
        <Row>
            <Spin spinning={loading} indicator={<AppLoader />}>
            <Row className='m-b-sm'>
                <Col span={24}>
                    <>Post installation URL<Tooltip title="Format: {Domain}/user/tools/github/postInstallation/{apiKey}"><InfoIcon className="m-l-xs" /></Tooltip></>
                </Col>
                <Col span={24}>
                    <Typography.Text copyable={{ text: postURL }} >{postURL}</Typography.Text>
                </Col>
            </Row>
            <Row className='m-b-sm'>
                <Col span={24}>
                <>Webhook URL<Tooltip title="Format: {Domain}/public/webHookEvent/{apiKeyID}"><InfoIcon className="m-l-xs" /></Tooltip></>
                </Col>
                <Col span={24}>
                    <Typography.Text copyable={{ text: webHookURL }} >{webHookURL}</Typography.Text>
                </Col>
            </Row>
            <Row className='m-b-sm'>
                <Col span={24}>
                    Webhook Secret
                </Col>
                <Col span={24}>
                    <Typography.Text copyable={{ text: secret }} >{secret}</Typography.Text>
                </Col>
            </Row>
            <Col span={24}>
                <Button onClick={onSave} type="primary"><GitHubIcon style={{fontSize: "1.3em"}} /> &nbsp;Connect To GitHub</Button>
            </Col>
            </Spin>
        </Row>
    );
};